import React, { useState, createContext, useContext } from 'react';
import type { UserProfile } from '@/types';
import { useToast } from './ToastContext';
import { authApi, setAccessToken } from '@/services/api';

interface AppContextType {
  userProfile: UserProfile | null;
  isAuthenticated: boolean;

  login: (email: string, password: string) => Promise<boolean>;
  loginWithOtp: (userId: string, code: string) => Promise<boolean>;
  register: (
    name: string,
    email: string,
    password: string,
    phone: string,
    role: UserProfile['role']
  ) => Promise<boolean>;
  verifyOTP: (otp: string) => Promise<boolean>;
  logout: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within an AppProvider');
  return context;
};

export const AppProvider = ({ children }: React.PropsWithChildren) => {
  const { showToast } = useToast();

  const [userProfile, setUserProfile] = useState<UserProfile | null>(() => {
    const saved = localStorage.getItem('bp_session');
    return saved ? JSON.parse(saved) : null;
  });

  const applySession = (data: import('@/services/api').LoginResponse) => {
    setAccessToken(data.accessToken);
    const user: UserProfile = {
      id: data.user.id,
      name: data.user.name,
      email: data.user.email,
      role: data.user.role.toLowerCase() as UserProfile['role'],
      status: 'active',
      isVerified: data.user.isVerified,
      trustScore: data.user.trustScore,
      phone: (data.user as { phone?: string }).phone ?? '',
      budgetMin: 0,
      budgetMax: 0,
      preferredLocations: [],
      lifestyle: '',
      avatar: data.user.avatar ?? undefined,
    };
    setUserProfile(user);
    localStorage.setItem('bp_session', JSON.stringify(user));
    showToast(`Welcome back, ${user.name}`, 'success');
  };

  const errMsg = (err: unknown, fallback: string) =>
    err && typeof err === 'object' && 'response' in err
      ? (err as { response: { data: { error: string } } }).response?.data?.error ?? fallback
      : fallback;

  const login = async (email: string, pass: string): Promise<boolean> => {
    try {
      const { data } = await authApi.login({ email, password: pass });
      applySession(data);
      return true;
    } catch (err: unknown) {
      showToast(errMsg(err, 'Login failed'), 'error');
      return false;
    }
  };

  const loginWithOtp = async (userId: string, code: string): Promise<boolean> => {
    try {
      const { data } = await authApi.otpLogin(userId, code);
      applySession(data);
      return true;
    } catch (err: unknown) {
      showToast(errMsg(err, 'Invalid or expired code'), 'error');
      return false;
    }
  };

  const register = async (
    name: string,
    email: string,
    pass: string,
    phone: string,
    role: UserProfile['role']
  ): Promise<boolean> => {
    try {
      const { data } = await authApi.register({ name, email, password: pass, phone, role: role.toUpperCase() as UserProfile['role'] });
      showToast('Registration successful! Check your email for a verification code.', 'success');
      localStorage.setItem('bp_pending_user_id', data.userId);
      return true;
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response: { data: { error: string } } }).response?.data?.error
          : 'Registration failed';
      showToast(msg ?? 'Registration failed', 'error');
      return false;
    }
  };

  const verifyOTP = async (otp: string): Promise<boolean> => {
    try {
      const userId = localStorage.getItem('bp_pending_user_id');
      if (!userId) return false;
      await authApi.verifyOTP({ userId, code: otp, purpose: 'email_verify' });
      localStorage.removeItem('bp_pending_user_id');
      return true;
    } catch {
      return false;
    }
  };

  const logout = () => {
    authApi.logout().catch(() => { /* best-effort */ });
    setAccessToken(null);
    setUserProfile(null);
    localStorage.removeItem('bp_session');
    showToast('Logged out successfully', 'info');
  };

  return (
    <AppContext.Provider
      value={{
        userProfile,
        isAuthenticated: !!userProfile,
        login,
        loginWithOtp,
        register,
        verifyOTP,
        logout,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
